/**
 * PORTAL ANALYTICS — what people see and click on the shop, for the portal's
 * heatmaps (research → "Where people look and click"), its storefront hub
 * (how each slide and menu picture did) and each order's Journey tab.
 *
 * Philip, 27 Sep 2026: "Will the pixel show us … what buttons they pressed",
 * then "Yes ok build it … It would be good to capture heatmap style data for
 * analytics, campaigns and storefront design helpfulness".
 *
 * NOTHING IS SENT until the visitor agrees to analytics (Shopify's own
 * customer-privacy consent). Then, for each page:
 *   - the buttons pressed, named from a fixed list of the shop's own buttons
 *     (BUTTONS below);
 *   - what each click was on, in the shop's own words — a link's or button's
 *     label, a card's title, an option's value — and where a link goes
 *     (wordsOf / whereTo; Philip, 1 Oct 2026: "everything on the pages should
 *     record what was actually clicked");
 *   - where each click landed within its section, slide or menu picture
 *     (a percentage of its width and height), clicks on things that do
 *     nothing, and three or more clicks in one spot in quick succession;
 *   - how far down the page they got;
 *   - which sections, slides and menu pictures were on their screen for a
 *     second or more (a slide only while it is the one showing, a menu
 *     picture only while its menu is open), and the time the page was.
 * A visitor who agreed is not censored (Philip, 28 Oct 2026, of an order's
 * Journey reading "Pressed a button … Clicked ×4": "what button was pressed?
 * where did they click, did they just click something random … if they
 * agree to cookies we can record everything possible that will help us"):
 * a click's words are whatever it landed on — a button's label, a picture's
 * description, the text itself, the personaliser's own words — with the
 * element it was (e: "span.product-meta__price") and how far down the page
 * (py). What stays out, consent or not: account and order pages, the
 * account link (the customer's own name), the email-choices and review
 * pages' own parts (a person's choices, a reviewer's words — each has its
 * own form), and the value of any email, phone or password box. A slide's
 * or menu picture's title comes from the theme's own data-pa-label, written
 * from the shop's settings.
 *
 * WHAT IS TYPED into the shop's search boxes and the personaliser's boxes
 * goes for everyone, agreed or not (TYPED, below): with Shopify's visitor id
 * when they agreed, so the order's Journey shows it in its place; with no id
 * at all when they did not.
 *
 * Sent as one batch per page every 20 seconds and when the page is left —
 * only what is new since the last — to the portal's data address (the
 * Worker at data.wedomugs.com, then a Cloudflare queue): event 'portal_page',
 * data.heat. The portal side: lib/storefront/heat.js. A page come back to
 * with Back or Forward (the browser's page cache: nothing loads, no script
 * starts again) is a page view of its own — 28 Oct 2026, order 3EECD66: a
 * product page returned to two minutes later sent nothing, and its clicks
 * were left as the pixel's "Pressed a button".
 *
 * Wired in layout/theme.liquid, after window.PortalAnalytics = { t, sx, v }
 * (the template's name and suffix, which the theme prints nowhere else, and
 * the theme's version number — snippets/portal-version.liquid, which the
 * portal writes and its releases carry to the live theme — so research can
 * set one version of the shop against the next).
 */
(function () {
  'use strict';
  if (window.__portalAnalytics) return;
  window.__portalAnalytics = true;

  var ENDPOINT = 'https://data.wedomugs.com/v1/e';
  var TYPED_ENDPOINT = 'https://data.wedomugs.com/v1/typed';
  var CFG0 = window.PortalAnalytics || {};
  var EVERY = Number(CFG0.every) || 20000;       // a batch at most this often
  var QUIET = Number(CFG0.quiet) || 120000;      // …and with only time to add, this often
  var MAX_CLICKS = 40;        // clicks per batch (the rest go in the next)
  var SEEN_MS = 1000;         // on screen this long counts as seen
  var TICK = 500;
  var DEPTHS = [25, 50, 75, 90, 100];

  function shop() { return window.Shopify || {}; }
  if (shop().designMode) return;   // not in the theme editor

  // ── consent ────────────────────────────────────────────────────────────
  function allowed() {
    try {
      var cp = shop().customerPrivacy;
      return !!(cp && typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed());
    } catch (e) { return false; }
  }
  var started = false;
  function tryStart() { if (!started && allowed()) { started = true; start(); } }
  document.addEventListener('visitorConsentCollected', function () { setTimeout(tryStart, 0); });
  function boot() {
    if (shop().customerPrivacy) return tryStart();
    var S = shop();
    if (typeof S.loadFeatures === 'function') {
      try { S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) { if (!err) tryStart(); }); } catch (e) { /* no consent API: nothing sent */ }
    }
  }

  // ── who and where ──────────────────────────────────────────────────────
  function rid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 10); }
  function cookie(n) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + n + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  // Shopify's own id for the browser — the one its pixels are given as
  // clientId — so a visit's pixel events and these batches are one visitor
  // (the order's Journey joins them by it). It is Shopify's analytics
  // library's visitor token (the _shopify_y cookie is not readable from the
  // page any more — seen 27 Sep 2026); our own id only when neither is
  // there. Read only after consent.
  var client = null;
  function shopifyToken() {
    try {
      var A = window.ShopifyAnalytics;
      var t = A && A.lib && typeof A.lib.user === 'function' && A.lib.user().traits().uniqToken;
      return t ? String(t) : (cookie('_shopify_y') || null);
    } catch (e) { return cookie('_shopify_y') || null; }
  }
  function clientId() {
    if (client) return client;
    var t = shopifyToken();
    if (t) return (client = t);
    try { var own = localStorage.getItem('tw_pa_client'); if (!own) { own = 'pa-' + rid(); localStorage.setItem('tw_pa_client', own); } return own; } catch (e) { return 'pa-' + rid(); }
  }
  function device() { var w = window.innerWidth || 0; return w < 641 ? 'phone' : (w < 1000 ? 'tablet' : 'desktop'); }
  function clip(v, n) { return String(v == null ? '' : v).slice(0, n); }
  function pct(v) { return Math.max(0, Math.min(100, Math.round(v * 10) / 10)); }

  // The section a thing is in: its own area when it is one of the header's
  // drawers and menus, else the Shopify section it sits in — its id without
  // the theme's number (the same on every copy of the theme), its type from
  // the theme's data-section-type.
  var AREAS = [
    ['#mini-cart', 'mini-cart'], ['.mega-menu', 'mega-menu'], ['#mobile-menu', 'mobile-menu'],
    ['.search-bar', 'search-bar'], ['[data-towsons-delivery][data-context="banner"]', 'delivery-banner'],
    ['.pswp', 'zoom'], ['[data-section-type="product-quick-view"]', 'quick-view'],
  ];
  var ORDER = [];
  function stableId(id) { return String(id || '').replace(/^shopify-section-/, '').replace(/^(template|sections)--\d+__/, ''); }
  function typeFromKey(key) {
    if (!key || /^[0-9a-f]{8}-|^\d/.test(key)) return '';
    return key.replace(/_[A-Za-z0-9]{6}$/, '').replace(/_/g, '-').replace(/-\d+$/, '');
  }
  function typeFromClass(el) { var m = /(?:^|\s)shopify-section--([a-z0-9-]+)/.exec(el.className || ''); return m ? m[1] : ''; }
  function areaOf(el) {
    if (!el || !el.closest) return null;
    var wrap = el.closest('.shopify-section');
    var full = wrap ? String(wrap.id || '').replace(/^shopify-section-/, '') : '';
    var i = wrap ? ORDER.indexOf(wrap) : -1;
    for (var a = 0; a < AREAS.length; a++) {
      var hit = el.closest(AREAS[a][0]);
      if (hit) return { el: hit, sid: AREAS[a][1], s: AREAS[a][1], full: full, i: i };
    }
    if (!wrap) return null;
    // The type is on the section's inner root: above a click, below the
    // section itself (what "seen" watches).
    var typed = el.closest('[data-section-type]');
    if (!typed || !wrap.contains(typed)) typed = wrap.querySelector('[data-section-type]');
    var type = (typed && typed.getAttribute('data-section-type')) || typeFromKey(stableId(wrap.id)) || typeFromClass(wrap);
    return { el: wrap, sid: clip(stableId(wrap.id), 100), s: clip(type, 60), full: full, i: i };
  }
  // The slide, menu picture or product block a thing is in.
  function blockOf(el, area) {
    var b = el.closest('[data-pa-block]');
    if (b && area && area.el.contains(b)) {
      var k = String(b.getAttribute('data-pa-block') || '');
      if (area.full && k.indexOf(area.full + '-') === 0) k = k.slice(area.full.length + 1);
      return { el: b, k: clip(k, 100), l: clip(b.getAttribute('data-pa-label') || '', 120) || null };
    }
    var pb = el.closest('[data-block-type]');
    if (pb && area && area.el.contains(pb)) return { el: pb, k: clip(pb.getAttribute('data-block-type'), 100), l: null };
    return null;
  }

  // THE SHOP'S OWN BUTTONS — first match wins, so the specific come first.
  // Named by what they do; the words on them are never read.
  var BUTTONS = [
    ['.shopify-payment-button, shopify-accelerated-checkout, [data-shopify="payment-button"]', 'buy_now'],
    ['.product-item buy-button button, .product-item__quick-add', 'quick_add'],
    ['buy-button button[type="submit"], .product-form__add-button', 'add_to_basket'],
    ['[data-action="increase-picker-quantity"]', 'quantity_up'],
    ['[data-action="decrease-picker-quantity"]', 'quantity_down'],
    ['.mini-cart__quantity-remove, .line-item__quantity-remove', 'remove_line'],
    ['[data-action="increase-quantity"]', 'basket_quantity_up'],
    ['[data-action="decrease-quantity"]', 'basket_quantity_down'],
    ['[data-listbox-button]', 'open_options'],
    ['.towsons-listbox__option', 'option'],
    ['.towsons-personaliser__swatch', 'personalise_colour'],
    ['.towsons-personaliser__view', 'personalise_view'],
    ['input.towsons-personaliser__input', 'personalise_name'],
    ['.towsons-personaliser__edit, [data-line-edit-open]', 'edit_personalisation'],
    ['[data-line-edit-done]', 'save_personalisation'],
    ['.towsons-treats', 'treat'],
    ['[data-action="toggle-mini-cart"]', 'open_basket'],
    ['button[name="checkout"], .cart-recap__checkout', 'checkout'],
    ['#mini-cart a.button--secondary', 'view_basket'],
    ['[data-action="save-note"]', 'cart_note'],
    ['[data-action="toggle-menu"]', 'open_menu'],
    ['[data-action="open-panel"]', 'menu_panel'],
    ['[data-action="close-panel"]', 'menu_back'],
    ['.mega-menu__promo, .mobile-menu__promo, .wdm-promo', 'menu_picture'],
    ['.nav-bar__link, .mega-menu__title, .mega-menu__link, .mobile-menu__nav-link, .nav-dropdown__link', 'menu_link'],
    ['.search-bar__submit', 'search'],
    ['.search-bar__view-all', 'search_all'],
    ['.search-bar__result-link, .search-bar__result-item', 'search_result'],
    ['.search-bar__menu-link', 'search_quick_link'],
    ['[data-action="clear-input"]', 'search_clear'],
    ['.search-bar__input', 'search_box'],
    ['.header__action-item--account, shopify-account, .tw-menu-account a', 'account'],
    ['.header__logo-link', 'logo'],
    ['.slideshow__button, a.slideshow__slide', 'slide_link'],
    ['.flickity-page-dots .dot', 'slide_dot'],
    ['.flickity-prev-next-button, .flickity-button', 'slide_arrow'],
    ['.product-gallery__thumbnail', 'gallery'],
    ['.pswp__button', 'zoom_nav'],
    ['.product-gallery__carousel-item, .product-gallery__image', 'zoom'],
    ['.expandable-content__toggle', 'read_more'],
    ['.product-meta__reviews-badge, .jdgm-widget, [class*="jdgm-"]', 'reviews'],
    ['.newsletter__form button[type="submit"], .newsletter__form .form__submit', 'newsletter'],
    ['[name^="filter."]', 'filter'],
    ['[data-action="clear-filters"]', 'clear_filters'],
    ['[data-action="open-drawer"]', 'filters_open'],
    ['#sort-by-selector [data-action="select-value"]', 'sort'],
    ['[data-action="change-layout"]', 'layout'],
    ['.pagination a', 'page_number'],
    ['[data-secondary-action="open-quick-view"]', 'quick_view'],
    ['.product-item__image-wrapper, .product-item__title', 'product_card'],
    ['.collection-item, .list-collections__item, .collection-list__item', 'collection_card'],
    ['.footer__title', 'footer_section'],
    ['.footer__link-item', 'footer_link'],
    ['.faq__question', 'faq'],
    ['.towsons-freeship', 'free_delivery_bar'],
    ['[data-towsons-delivery]', 'delivery_promise'],
  ];
  function buttonOf(el) {
    for (var i = 0; i < BUTTONS.length; i++) { try { if (el.closest(BUTTONS[i][0])) return BUTTONS[i][1]; } catch (e) { /* a selector this browser cannot read */ } }
    return null;
  }
  var ACTS = 'a[href], button, input, select, textarea, label, summary, [role="button"], [role="option"], [role="tab"], [role="radio"], [data-action], [tabindex]:not([tabindex="-1"]), [onclick], shopify-accelerated-checkout, shopify-account';
  function doesSomething(el) { try { return !!el.closest(ACTS); } catch (e) { return true; } }

  // WHAT WAS CLICKED, IN WORDS (Philip, 1 Oct 2026, on an order's Journey
  // showing "Clicked ×4": "we need to know exactly what the user clicked,
  // everything on the pages should record what was actually clicked"). Each
  // click carries the words naming the thing clicked (w) and, for a link,
  // where it goes (h): a link's or button's label, a picture's description, a
  // card's title, an option's value, a field's label — and, since 28 Oct 2026,
  // whatever text was clicked when nothing that can be pressed was (Philip:
  // "did they just click something random"; the visitor agreed to analytics,
  // and "we can record everything possible that will help us"), the
  // personaliser's and the discover module's words and a basket line's
  // details among it. Never, agreed or not: account and order pages, the
  // account link (the customer's own name), the email-choices and review
  // pages' own parts (data-tep, data-trf: a person's choices, a reviewer's
  // words — each has its own form), anything the theme marks data-pa-private,
  // and the value of any field (what is typed goes as TYPED, below, and
  // never an email, phone or password box's).
  var NO_WORDS = '[data-pa-private], .header__action-item--account, shopify-account, [data-tep], [data-trf]';
  var CARDS = '.product-item, .collection-item, .list-collections__item, .collection-list__item, .line-item, .mini-cart__item-wrapper';
  var CARD_TITLES = '.product-item__title, .collection-item__title, .list-collections__item-title, .collection-list__item-title, .line-item__title, .line-item__product-title, .mini-cart__product-title';
  var FIELDS = 'input, textarea, select';
  function privatePage() { return /^\/(account|orders)(\/|$)|\/orders\//.test(location.pathname || ''); }
  function squash(s) { return String(s == null ? '' : s).replace(/\s+/g, ' ').trim(); }
  function textOf(el) { return squash(el.textContent); }
  function nameAttr(el) { return el && el.getAttribute ? (el.getAttribute('data-pa-label') || el.getAttribute('aria-label') || el.getAttribute('title') || '') : ''; }
  // A field by its label — never by what is in it.
  function fieldName(f) {
    var named = f.getAttribute('aria-label') || f.getAttribute('placeholder') || f.getAttribute('title');
    if (named) return named;
    if (f.id) { try { var lab = document.querySelector('label[for="' + String(f.id).replace(/["\\]/g, '\\$&') + '"]'); if (lab) return textOf(lab); } catch (e) { /* an id no selector can hold */ } }
    var wrap = f.closest('label');
    return wrap ? textOf(wrap) : '';
  }
  function wordsOf(el) {
    if (privatePage()) return null;
    try { if (el.closest(NO_WORDS)) return null; } catch (e) { return null; }
    var f = el.closest(FIELDS);
    if (f) return clip(squash(fieldName(f)), 80) || null;
    var act = null;
    try { act = el.closest(ACTS); } catch (e) { act = null; }
    var card = el.closest(CARDS);
    // Plain text, pressed: the text itself (a whole block of it names nothing), or a picture's description; on a
    // card, with the card's title ("£9.99 · Black Mug").
    if (!act) {
      var ct = card ? card.querySelector(CARD_TITLES) : null;
      var said = textOf(el);
      if (said.length > 120) said = '';
      if (!said && el.tagName === 'IMG') said = squash(el.getAttribute('alt'));
      var title0 = ct ? textOf(ct) : '';
      return clip(said && title0 && said !== title0 ? said + ' · ' + title0 : (said || title0), 80) || null;
    }
    var t = act;
    if (t.tagName === 'LABEL' && t.control) return clip(squash(fieldName(t.control) || textOf(t)), 80) || null;
    var own = squash(nameAttr(t));
    if (!own) {
      var txt = textOf(t);
      if (txt && txt.length <= 120) own = txt;
    }
    if (!own) {
      var img = t.tagName === 'IMG' ? t : (t.querySelector ? t.querySelector('img[alt]') : null);
      if (img && img.getAttribute('alt')) own = squash(img.getAttribute('alt'));
    }
    // On a card \u2014 a product, a category, a basket line \u2014 whichever part was
    // clicked, it is named by the card's title; a button on it by both
    // ("Quick add \u00b7 Black Mug", "Remove \u00b7 Black Mug").
    var title = card ? card.querySelector(CARD_TITLES) : null;
    var named = title ? textOf(title) : '';
    if (named) {
      var button = t.tagName === 'BUTTON' || (t.getAttribute('role') || '') === 'button';
      own = button && own && own !== named ? own + ' \u00b7 ' + named : named;
    }
    return clip(own, 80) || null;
  }
  // Where a link goes: its path on the shop, or the other site's name.
  function whereTo(el) {
    var a = null;
    try { a = el.closest('a[href]'); } catch (e) { a = null; }
    if (!a || privatePage()) return null;
    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#' || /^(javascript|mailto|tel):/i.test(href)) return null;
    try { var u = new URL(href, location.href); return clip(u.host === location.host ? u.pathname : u.host, 120); } catch (e) { return null; }
  }
  // The element itself, as the page names it: its tag and first own class ("span.product-meta__price",
  // "img.product-gallery__image") — what a click with no button kind landed on, so the Journey can say "the price"
  // rather than "Clicked". An icon's parts are the icon's holder.
  function elementOf(el) {
    var svg = el.closest ? el.closest('svg') : null;
    var t = (svg && svg.parentElement) || el;
    var list = String((t.getAttribute && t.getAttribute('class')) || '').split(/\s+/);
    var cls = '';
    for (var i = 0; i < list.length; i++) { if (list[i] && !/^(is|js|has)-/.test(list[i])) { cls = list[i]; break; } }
    return clip(String(t.tagName || '').toLowerCase() + (cls ? '.' + cls : ''), 60) || null;
  }
  function pageHeight() {
    var doc = document.documentElement;
    return Math.max(doc.scrollHeight || 0, document.body ? document.body.scrollHeight : 0);
  }

  // ── the page ───────────────────────────────────────────────────────────
  var T0 = (window.performance && performance.timeOrigin) ? Math.round(performance.timeOrigin) : Date.now();
  var PAGE = { pid: rid(), seq: 0, isNew: true, clicks: [], seen: [], reached: [], got: {}, visMs: 0, visFrom: null, lastSent: 0 };

  // ── a person, or a script? ────────────────────────────────────────────────
  // Philip, 7 Oct 2026: recordings of "a product page … landed on, scrolls to the bottom and then a new product page
  // is loaded … looks like bot activity". A person cannot scroll a page without doing something only a person does —
  // a mouse that moves, a touch, a key, a wheel — and the browser marks those as its own (isTrusted). A page that
  // scrolled well after it loaded (not the browser putting the reader back where they were) with none of them was
  // scrolled by a script: its events say so (bh 'scripted'), as does a browser that says it is automated
  // (navigator.webdriver: 'webdriver'). The shop edge marks such a visit a bot, and the portal leaves it out of the
  // people it counts. Saying so only takes a visitor out of the counts; nothing is refused for it.
  var HUMAN = { yes: false, settledAt: 0, y0: null, late: false };
  function onHuman(e) {
    if (HUMAN.yes || !e || e.isTrusted === false) return;
    if ((e.type === 'mousemove' || e.type === 'pointermove') && !e.movementX && !e.movementY) return;
    HUMAN.yes = true;
  }
  ['pointermove', 'mousemove', 'pointerdown', 'touchstart', 'keydown', 'wheel'].forEach(function (t) { window.addEventListener(t, onHuman, { capture: true, passive: true }); });
  function settle() { setTimeout(function () { HUMAN.settledAt = Date.now(); HUMAN.y0 = window.scrollY || window.pageYOffset || 0; }, 1500); }
  if (document.readyState === 'complete') settle(); else window.addEventListener('load', settle);
  window.addEventListener('scroll', function () {
    if (HUMAN.late || !HUMAN.settledAt || HUMAN.y0 == null) return;
    if (Math.abs((window.scrollY || window.pageYOffset || 0) - HUMAN.y0) >= 300) HUMAN.late = true;
  }, { passive: true });
  function botHint() {
    try { if (navigator.webdriver === true) return 'webdriver'; } catch (e) { /* no signal */ }
    return !HUMAN.yes && HUMAN.late ? 'scripted' : null;
  }
  var recent = [];   // the last few clicks, for rage

  function onClick(e) {
    var el = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
    if (!el || !el.closest) return;
    var area = areaOf(el);
    var b = buttonOf(el);
    var c = { at: Date.now() - T0 };
    if (b) c.b = b;
    var w = wordsOf(el);
    if (w) c.w = w;
    var h = whereTo(el);
    if (h) c.h = h;
    if (!b && !privatePage()) { var en = elementOf(el); if (en) c.e = en; }
    // How far down the whole page (none for a keyboard press).
    var ph = pageHeight();
    if (e.detail !== 0 && e.pageY > 0 && ph > 0) c.py = pct((e.pageY / ph) * 100);
    if (area) {
      c.s = area.s; c.sid = area.sid;
      var blk = blockOf(el, area);
      if (blk) { c.k = blk.k; if (blk.l) c.l = blk.l; }
      // Where in the section: none for a keyboard press (it has no place).
      if (e.detail !== 0 && (e.clientX || e.clientY)) {
        var r = area.el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) { c.x = pct(((e.clientX - r.left) / r.width) * 100); c.y = pct(((e.clientY - r.top) / r.height) * 100); }
      }
    }
    if (!b && !doesSomething(el)) c.dead = 1;
    // Three or more in one spot within 0.8 s: somebody frustrated.
    var now = Date.now();
    recent.push({ t: now, x: e.clientX || 0, y: e.clientY || 0 });
    recent = recent.filter(function (p) { return now - p.t < 800; });
    if (recent.length >= 3 && recent.every(function (p) { return Math.abs(p.x - (e.clientX || 0)) < 30 && Math.abs(p.y - (e.clientY || 0)) < 30; })) c.rage = 1;
    PAGE.clicks.push(c);
    if (PAGE.clicks.length >= MAX_CLICKS) flush(false);
  }

  // Seen: on screen (half of it, or half the screen when it is taller) for
  // SEEN_MS, while it can be seen at all.
  var watched = [];
  var io = null;
  function shown(el) {
    if (el.classList.contains('slideshow__slide') && !el.classList.contains('is-selected')) return false;
    var mm = el.closest('.mega-menu');
    if (mm && mm.getAttribute('aria-hidden') !== 'false') return false;
    var mob = el.closest('#mobile-menu');
    if (mob) {
      if (mob.getAttribute('aria-hidden') !== 'false') return false;
      var panel = el.closest('.mobile-menu__panel');
      if (panel && !panel.classList.contains('is-open')) return false;
    }
    var mc = el.closest('#mini-cart');
    if (mc && mc.getAttribute('aria-hidden') !== 'false') return false;
    return !!(el.getClientRects && el.getClientRects().length);
  }
  function watch(el) {
    if (el.__pa) return;
    el.__pa = { inView: false, ms: 0, done: false };
    watched.push(el);
    if (io) io.observe(el);
  }
  function scan() {
    ORDER = Array.prototype.slice.call(document.querySelectorAll('.shopify-section'));
    ORDER.forEach(watch);
    Array.prototype.forEach.call(document.querySelectorAll('[data-pa-block]'), watch);
  }
  function tick() {
    var visible = document.visibilityState === 'visible';
    if (!visible) return;
    watched.forEach(function (el) {
      var w = el.__pa;
      if (w.done || !w.inView || !el.isConnected || !shown(el)) return;
      w.ms += TICK;
      if (w.ms < SEEN_MS) return;
      w.done = true;
      var area = areaOf(el);
      if (!area) return;
      var s = { s: area.s, sid: area.sid, i: area.i };
      if (el.hasAttribute('data-pa-block')) { var blk = blockOf(el, area); if (blk) { s.k = blk.k; if (blk.l) s.l = blk.l; } }
      PAGE.seen.push(s);
    });
    depth();
  }

  function depth() {
    var doc = document.documentElement;
    var h = pageHeight();
    if (!h) return;
    var seen = ((window.scrollY || doc.scrollTop || 0) + (window.innerHeight || 0)) / h * 100;
    DEPTHS.forEach(function (d) {
      if (!PAGE.got[d] && seen >= (d === 100 ? 98 : d)) { PAGE.got[d] = true; PAGE.reached.push(d); }
    });
  }

  function visibleNow() {
    if (PAGE.visFrom != null) { PAGE.visMs += Date.now() - PAGE.visFrom; PAGE.visFrom = null; }
    if (document.visibilityState === 'visible') PAGE.visFrom = Date.now();
  }

  // ── sending ────────────────────────────────────────────────────────────
  function send(body, url) {
    url = url || ENDPOINT;
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) return;
    } catch (e) { /* a fetch instead */ }
    try { fetch(url, { method: 'POST', body: body, keepalive: true, credentials: 'omit', headers: { 'Content-Type': 'text/plain' } }); } catch (e) { /* lost: the shop is not slowed */ }
  }

  // A page come back to with Back or Forward: the browser kept it whole (its page cache), so no script starts again
  // and the pixel may or may not say it was viewed. It is a page view of its own — a new id, its own start, depth and
  // sections seen — so its clicks are its own and the Journey puts them under it.
  function newPage() {
    T0 = Date.now();
    PAGE = { pid: rid(), seq: 0, isNew: true, clicks: [], seen: [], reached: [], got: {}, visMs: 0, visFrom: null, lastSent: 0 };
    watched.forEach(function (el) { if (el.__pa) { el.__pa.ms = 0; el.__pa.done = false; } });
    recent = [];
    visibleNow();
    depth();
    flush(false);
  }

  function flush(leaving) {
    if (!started) return;
    visibleNow();
    if (leaving) PAGE.visFrom = null;
    var theme = shop().theme || {};
    var cfg = window.PortalAnalytics || {};
    // Nothing new but time on screen: kept for a later batch — sent only
    // every QUIET, or when the page is left (a page left open is not a
    // batch every 20 seconds).
    var fresh = PAGE.isNew || PAGE.clicks.length || PAGE.seen.length || PAGE.reached.length;
    if (!fresh && (leaving ? PAGE.visMs <= 0 : (PAGE.visMs < 1000 || Date.now() - PAGE.lastSent < QUIET))) return;
    var clicks = PAGE.clicks.splice(0, MAX_CLICKS);
    var seen = PAGE.seen.splice(0, 50);
    var reached = PAGE.reached.splice(0);
    var ms = PAGE.visMs;
    PAGE.visMs = 0;
    var heat = {
      pid: PAGE.pid, t: clip(cfg.t || (window.theme && window.theme.pageType) || '', 40),
      th: clip(theme.role || '', 20), tid: theme.id || null, dev: device(),
      start: new Date(T0).toISOString(), ms_add: ms,
    };
    if (cfg.sx) heat.sx = clip(cfg.sx, 40);
    if (cfg.v > 0 && cfg.v < 1e9) heat.v = Math.floor(cfg.v);
    if (PAGE.isNew) heat.new = 1;
    if (reached.length) heat.reached = reached;
    if (clicks.length) heat.clicks = clicks;
    if (seen.length) heat.seen = seen;
    PAGE.isNew = false;
    PAGE.lastSent = Date.now();
    send(JSON.stringify({
      id: PAGE.pid + '.' + (PAGE.seq++), name: 'portal_page', type: 'custom', at: new Date().toISOString(),
      client: clientId(), page: { host: location.hostname, path: location.pathname },
      agent: navigator.userAgent, width: window.innerWidth || null,
      bh: botHint(),
      data: { heat: heat },
    }));
  }

  // ── WHAT IS TYPED: the search boxes and the personaliser's ─────────────
  // Philip, 28 Oct 2026: "we don't actually need to censor what the user types into search or product customisation
  // boxes … separately we should actually be recording all search data … but also everything that's inputted into
  // personalisation boxes, even when it's anonymous if they deny cookies, it's still vital to know what's inputted
  // there". So what is typed into these boxes goes for EVERYONE, agreed or not, to the data address's /v1/typed:
  //   - agreed to analytics: with Shopify's visitor id and this page view's id, so an order's Journey shows it in
  //     its place among the clicks;
  //   - not agreed (declined, or not answered yet): ANONYMOUS — no visitor id, no page id, nothing read from a cookie
  //     or the browser's storage; only the words, which box, the page, the kind of device and when. The shop edge
  //     adds the country, refuses bots, and passes on nothing of the address or the browser.
  // Only these boxes (TYPED): the personaliser's (on the product page and a basket line's edit), the product form's
  // own custom boxes, the discover module's name box, and the search boxes. Never an email, phone, password or card
  // box, nothing in the sign-in, contact or newsletter forms, nothing on account or order pages.
  // What is kept of a box: its words once the typing pauses (1.5 s), when it is left, and when the page goes; what
  // was in the personaliser's boxes when Add to basket (or Buy it now) was pressed, and a basket line's when its edit
  // was saved; and on the search page, what was searched for and how many it found.
  var TYPED = [
    ['.towsons-personaliser__input, .towsons-personaliser__line-input', 'personalise'],
    ['[data-tdc-name]', 'discover'],
    ['[data-help-search-input]', 'help'],
    ['input[type="search"], .search-bar__input, input[name="q"], [data-tw-suggest]', 'search'],
    ['form[action*="/cart/add"] input[name^="properties["], form[action*="/cart/add"] textarea[name^="properties["]', 'personalise'],
  ];
  var NEVER_TYPED = 'input[type="password"], input[type="email"], input[type="tel"], input[type="hidden"], input[type="file"], input[type="checkbox"], input[type="radio"], [autocomplete~="email"], [autocomplete~="tel"], [autocomplete^="cc-"]';
  var NEVER_TYPED_IN = 'form[action*="/account"], form[action*="/contact"], form[action*="/customer"], .newsletter, [data-pa-private], [data-tep], [data-trf]';
  var TYPED_PAUSE = 1500;
  var TYPED_EVERY = 10000;
  var TYPED_MAX = 30;
  var TY = { items: [], last: {}, waiting: [] };
  function typedKind(f) {
    if (!f || !f.matches || privatePage()) return null;
    try { if (f.matches(NEVER_TYPED) || f.closest(NEVER_TYPED_IN)) return null; } catch (e) { return null; }
    for (var i = 0; i < TYPED.length; i++) { try { if (f.matches(TYPED[i][0])) return TYPED[i][1]; } catch (e) { /* a selector this browser cannot read */ } }
    return null;
  }
  // Which box: the personaliser's own label ("Name", "Message"), else the property it fills, else the box's label.
  function typedLabel(f, kind) {
    var m = /^properties\[(.+)\]$/.exec(f.getAttribute('name') || '');
    var prop = m ? m[1] : (f.getAttribute('data-property') || '');
    if (kind === 'personalise') {
      var wrap = f.closest('.towsons-personaliser__field, .towsons-personaliser__line-field');
      var lab = wrap && wrap.querySelector('.towsons-personaliser__label, .towsons-personaliser__line-label');
      var said = lab ? squash(lab.textContent).replace(/\s*\*$/, '') : '';
      return clip(said || prop || squash(fieldName(f)), 60) || null;
    }
    if (kind === 'search') return f.closest('.search-bar') ? 'search bar' : 'search page';
    return clip(squash(fieldName(f)), 60) || null;
  }
  function noteTyped(f, how, n) {
    var kind = typedKind(f);
    if (!kind) return;
    var v = squash(f.value);
    if (!v) return;
    var label = typedLabel(f, kind);
    var key = kind + '|' + (f.getAttribute('name') || '') + '|' + (f.getAttribute('data-slot') || f.getAttribute('data-line-slot') || '') + '|' + (label || '');
    var last = TY.last[key];
    if (how === 'typed' && last && last.v === v) return;
    TY.last[key] = { v: v, how: how };
    var it = { at: Date.now() - T0, k: kind, v: clip(v, 200), how: how };
    if (label) it.l = label;
    if (n != null && isFinite(Number(n))) it.n = Number(n);
    TY.items.push(it);
    if (TY.items.length >= TYPED_MAX) sendTyped();
  }
  function settleTyped() {
    var w = TY.waiting.splice(0);
    w.forEach(function (f) { clearTimeout(f.__paTyped); f.__paTyped = null; noteTyped(f, 'typed'); });
  }
  function sendTyped() {
    if (!TY.items.length) return;
    var cfg = window.PortalAnalytics || {};
    var body = {
      id: 'ty-' + rid(), at: new Date().toISOString(), start: new Date(T0).toISOString(),
      page: { host: location.hostname, path: location.pathname }, t: clip(cfg.t || '', 40), dev: device(),
      items: TY.items.splice(0, TYPED_MAX),
    };
    var bh = botHint();
    if (bh) body.bh = bh;
    // Agreed: whose, and which page view — only then is Shopify's id (or our own, in the browser's storage) read.
    if (started && allowed()) { body.consent = 1; body.client = clientId(); body.pid = PAGE.pid; }
    send(JSON.stringify(body), TYPED_ENDPOINT);
  }
  function onTyping(e) {
    if (!e || e.isTrusted === false) return;
    var f = e.target;
    if (!typedKind(f)) return;
    if (e.type === 'change') { clearTimeout(f.__paTyped); f.__paTyped = null; noteTyped(f, 'typed'); return; }
    clearTimeout(f.__paTyped);
    if (TY.waiting.indexOf(f) < 0) TY.waiting.push(f);
    f.__paTyped = setTimeout(function () {
      f.__paTyped = null;
      var i = TY.waiting.indexOf(f);
      if (i >= 0) TY.waiting.splice(i, 1);
      noteTyped(f, 'typed');
    }, TYPED_PAUSE);
  }
  // What the personaliser's boxes held as it went into the basket — or a basket line's, as its edit was saved.
  function onTypedPress(e) {
    if (!e || e.isTrusted === false) return;
    var el = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
    if (!el || !el.closest) return;
    var b = buttonOf(el);
    var how = (b === 'add_to_basket' || b === 'buy_now') ? 'added' : (b === 'save_personalisation' ? 'saved' : null);
    if (!how) return;
    var within = how === 'saved' ? (el.closest('.line-item, .mini-cart__item-wrapper, [data-line-edit], .towsons-personaliser__modal') || document) : document;
    var sel = how === 'saved' ? '.towsons-personaliser__line-input' : '.towsons-personaliser__input, form[action*="/cart/add"] input[name^="properties["], form[action*="/cart/add"] textarea[name^="properties["]';
    var seen = [];
    Array.prototype.forEach.call(within.querySelectorAll(sel), function (f) {
      if (seen.indexOf(f) >= 0 || !squash(f.value)) return;
      seen.push(f);
      noteTyped(f, how);
    });
    sendTyped();
  }
  function bootTyped() {
    if (privatePage()) return;
    document.addEventListener('input', onTyping, true);
    document.addEventListener('change', onTyping, true);
    document.addEventListener('click', onTypedPress, true);
    // The search page: what was searched for (however it got here — the box, a suggestion, "view all") and how many
    // it found (window.PortalAnalytics.rc, printed by the theme on the search template).
    var cfg = window.PortalAnalytics || {};
    if (cfg.t === 'search') {
      var q = null;
      try { q = new URLSearchParams(location.search).get('q'); } catch (e) { q = null; }
      q = squash(q);
      if (q) TY.items.push(Object.assign({ at: 0, k: 'search', l: 'search page', v: clip(q, 200), how: 'searched' }, cfg.rc != null && isFinite(Number(cfg.rc)) ? { n: Number(cfg.rc) } : {}));
    }
    setInterval(function () { sendTyped(); }, TYPED_EVERY);
    // Whatever is still being typed goes with the page: the box need not have paused.
    var going = function () { settleTyped(); sendTyped(); };
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') going(); });
    window.addEventListener('pagehide', going);
    // The search page's own line waits a moment for consent's answer, so a visitor who agrees has it as theirs.
    if (TY.items.length) setTimeout(sendTyped, 3000);
  }

  function start() {
    window.addEventListener('pageshow', function (e) { if (e && e.persisted) newPage(); });
    scan();
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var el = en.target;
          if (!el.__pa) return;
          var vh = window.innerHeight || 1;
          var h = en.boundingClientRect.height || 1;
          el.__pa.inView = en.isIntersecting && en.intersectionRect.height >= Math.min(h * 0.5, vh * 0.5);
        });
      }, { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });
      watched.forEach(function (el) { io.observe(el); });
    }
    visibleNow();
    depth();
    // Capture: the Add to basket button and the personaliser stop their
    // clicks before they reach the page.
    document.addEventListener('click', onClick, true);
    window.addEventListener('scroll', function () { if (!depth.q) { depth.q = true; requestAnimationFrame(function () { depth.q = false; depth(); }); } }, { passive: true });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') flush(true); else visibleNow();
    });
    window.addEventListener('pagehide', function () { flush(true); });
    setInterval(tick, TICK);
    setInterval(scan, 5000);           // what a script drew since (the basket drawer, filtered products)
    setInterval(function () { flush(false); }, EVERY);
    // The page, viewed — once Shopify's visitor token is there (its analytics
    // library loads after this script), or after 3 s without it.
    var waited = 0;
    (function first() {
      if (shopifyToken() || waited >= 3000) return flush(false);
      waited += 200;
      setTimeout(first, 200);
    })();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  // What is typed goes whether or not the visitor agrees (anonymously when not): its listeners from the start.
  bootTyped();
})();
